import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  CellControllerKind,
  ProgressBarControllerEntity,
} from 'entities/TableControllers';
import { debounce } from 'utils/debounce';

import { ITableControllerBase, TableControllerParams } from '../interface';

type PrivateFields = '_value';

const PROGRESS_BAR_CHANGE_DEBOUNCE_TIME = 800;

export default class TableProgressBarController
  implements
    ITableControllerBase<ProgressBarControllerEntity['controller']['value']>
{
  constructor({
    record,
    onSave,
  }: TableControllerParams<ProgressBarControllerEntity>) {
    this._value = record.controller;
    this._initialValue = record.controller;
    makeObservable<TableProgressBarController, PrivateFields>(this, {
      _value: observable.ref,
      controllerValue: computed,
      setValue: action.bound,

      toServerFormat: computed,

      reset: action.bound,
    });

    const onSaveHandler = debounce(async () => {
      await onSave();
      this._isUpdateBlocked = false;
    }, PROGRESS_BAR_CHANGE_DEBOUNCE_TIME);

    this._reactions.push(
      reaction(
        () => this._value,
        async () => {
          this._isUpdateBlocked = true;
          await onSaveHandler();
        }
      )
    );
  }

  private _value: ProgressBarControllerEntity['controller'];

  private readonly _reactions: IReactionDisposer[] = [];

  private readonly _initialValue: ProgressBarControllerEntity['controller'];

  private _isUpdateBlocked = false;

  get isUpdateBlocked(): boolean {
    return this._isUpdateBlocked;
  }

  get controllerValue() {
    return (100 * this._value.value) / this._value.maxValue;
  }

  get maxValue() {
    return this._value.maxValue;
  }

  get controllerKind() {
    return CellControllerKind.PROGRESS_BAR;
  }

  get toServerFormat() {
    return this._value.value;
  }

  reset(): void {
    this._value = this._initialValue;
  }

  destroy() {
    this._reactions.forEach((reaction) => reaction());
  }

  setValue(nextValue: number): void {
    if (nextValue === this._value.value) {
      return;
    }

    this._value = {
      maxValue: this._initialValue.maxValue,
      value: nextValue,
    };
  }
}
